import { builder, BuilderComponent, BuilderContent } from '@builder.io/react'
import type { BuilderContent as Content } from '@builder.io/sdk'
import type { NextPageContext } from 'next'
import { useRouter } from 'next/router'

import type { PropsWithChildren } from 'react'

import { FooterBlock } from '@/blocks/Footer'
import { HeaderBlock } from '@/blocks/Header'
import { ErrorContent } from '@/components/ErrorContent'
import { Metatags } from '@/components/Metatags'
import { PRODUCT_KEYS, type ProductKeys } from '@/components/Products/constants'
import { Theme } from '@/components/Theme'
import {
  BUILDER_ADS_MODEL,
  BUILDER_ADS_QUERY,
  BUILDER_FOOTER_MODEL,
  BUILDER_PAGE_MODEL,
} from '@/config/builder'
import { AnalyticsProvider } from '@/core/analytics'
import { getProductFromUrl } from '@/core/utils/getProductFromUrl'
import type { AnalyticsData } from '@/models'

const getUrlPathError = (errorCode: number, product: ProductKeys) =>
  `/builder/error/${product || PRODUCT_KEYS.serasa}/${errorCode}/`

export type ErrorPageProps = PropsWithChildren<{
  errorCode: number
  refreshUrl?: string
  product?: ProductKeys | null
  adsBottom?: Content
  page?: Content
  footer?: Content
  urlPath?: string
}>

const Error = ({
  errorCode,
  refreshUrl,
  product,
  adsBottom,
  page,
  footer,
  urlPath,
}: ErrorPageProps) => {
  const router = useRouter()

  if (errorCode === 500) {
    const url = refreshUrl || router.asPath

    const getAnalytics = () => {
      const GTM_CODE = process.env.NEXT_PUBLIC_GTM_CODE as string
      const GTM_VERTICAL_PRODUCT = 'serasa-web'

      return {
        gtmCode: {
          code: GTM_CODE,
        },
        verticalProduct: GTM_VERTICAL_PRODUCT,
      }
    }

    return (
      <AnalyticsProvider event={{ ...getAnalytics(), url, position: 'meio' }}>
        <ErrorContent errorCode={errorCode} refreshUrl={url} />
      </AnalyticsProvider>
    )
  }

  const productFilter = product || getProductFromUrl(router.asPath)
  const urlPathFilter = urlPath || getUrlPathError(errorCode, productFilter)

  return (
    <BuilderContent
      model={BUILDER_PAGE_MODEL}
      {...(page && { content: page })}
      options={{
        userAttributes: { urlPath: urlPathFilter },
        query: {
          'data.product': { $in: [productFilter, PRODUCT_KEYS.serasa] },
        },
      }}
    >
      {(data, loading, content) => {
        if (loading) {
          return null
        }

        const url = refreshUrl || data?.url || router.asPath
        const analytics: AnalyticsData = data?.analytics

        return (
          <Theme data={data}>
            <Metatags data={{ ...data, robots: false }} />
            <AnalyticsProvider event={{ ...analytics, url, position: 'topo' }}>
              <HeaderBlock data={data} />
            </AnalyticsProvider>
            <AnalyticsProvider event={{ ...analytics, url, position: 'meio' }}>
              <ErrorContent errorCode={errorCode} refreshUrl={refreshUrl} />
              <BuilderComponent
                model={BUILDER_PAGE_MODEL}
                options={{ includeRefs: true }}
                {...(content && { content })}
              />
            </AnalyticsProvider>
            <BuilderComponent
              model={BUILDER_ADS_MODEL}
              content={adsBottom}
              options={{ query: BUILDER_ADS_QUERY.bottom }}
            />
            <AnalyticsProvider event={{ ...analytics, url, position: 'rodape' }}>
              <FooterBlock content={footer} />
            </AnalyticsProvider>
          </Theme>
        )
      }}
    </BuilderContent>
  )
}

Error.getInitialProps = async ({ res, req, err }: NextPageContext) => {
  let errorCode = 404
  if (res?.statusCode) {
    errorCode = res.statusCode
  } else if (err) {
    errorCode = 500
  }
  try {
    const product = getProductFromUrl(req?.url)
    const urlPath = getUrlPathError(errorCode, product)

    const [page, footer] = await Promise.all([
      builder
        .get(BUILDER_PAGE_MODEL, {
          userAttributes: { urlPath },
          query: { 'data.product': { $in: [product, PRODUCT_KEYS.serasa] } },
          res,
          req,
        })
        .promise(),
      builder.get(BUILDER_FOOTER_MODEL, { userAttributes: { urlPath }, res, req }).promise(),
    ])

    return {
      refreshUrl: req?.url,
      page: page || null,
      footer: footer || null,
      errorCode,
      product,
      urlPath,
    }
  } catch (err) {
    return {
      errorCode,
      refreshUrl: req?.url,
    }
  }
}

export default Error
